import React, { type ReactNode } from 'react';

import { Text } from '@atlaskit/primitives/compiled';
import {
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';

import { PanelSkeleton } from '../components/panel-skeleton/panel-skeleton';

import { usePanel } from './panel-context';

export type PanelRouterProps = {
	/**
	 * Optional wrapper component to wrap the rendered entry point.
	 * Useful for adding styling, resizing functionality, or other wrapper behavior.
	 *
	 * @example
	 * ```tsx
	 * <PanelRouter
	 *   wrapper={({ children }) => (
	 *     <Box xcss={styles.panelWrapper}>{children}</Box>
	 *   )}
	 * />
	 * ```
	 */
	wrapper?: (props: { children: ReactNode }) => ReactNode;
	/**
	 * Fallback UI to show while the entry point is loading.
	 * Defaults to a skeleton state that indicates header and body content. Can be customised and composed with different skeleton components.
	 */
	fallback?: ReactNode;
	/**
	 * Error fallback function to render when entry point loading fails.
	 * Defaults to a simple "Error loading panel" text.
	 */
	errorFallback?: ErrorFallback;
};

/**
 * PanelRouter component that automatically renders entry points from panel state.
 *
 * This component is designed to be used as a child of PanelsRenderer. It:
 * - Gets the current panel from PanelContext (via usePanel)
 * - Checks if the panel has an entryPointReference
 * - Renders the entry point using InternalEntryPointContainer
 * - Passes panel params as runtimeProps to the entry point
 *
 * This is the recommended way to render entry points in panels when using
 * the Trigger component, which automatically loads and stores entry point
 * references in panel state.
 *
 * Must be used within a PanelsRenderer (which provides PanelContext).
 *
 * @example
 * ```tsx
 * <PanelsRenderer>
 *   <PanelRouter />
 * </PanelsRenderer>
 * ```
 *
 * @example
 * ```tsx
 * // With custom wrapper and fallbacks
 * <PanelsRenderer>
 *   <PanelRouter
 *     wrapper={({ children }) => (
 *       <Box xcss={styles.panelWrapper}>{children}</Box>
 *     )}
 *     fallback={<PanelSkeleton />}
 *     errorFallback={() => <ErrorState />}
 *   />
 * </PanelsRenderer>
 * ```
 */
export function PanelRouter({
	wrapper,
	fallback = <PanelSkeleton />,
	errorFallback = () => <Text as="p">Error loading panel</Text>,
}: PanelRouterProps): React.JSX.Element | null {
	const { panel } = usePanel();
	const { params, instanceId } = panel;

	// If no EntryPoint reference, render nothing
	// This means an EntryPoint has not been initialised, or a panel was opened without EntryPoints.
	if (!('entryPointReference' in panel) || !panel.entryPointReference) {
		return null;
	}

	const entryPointContent = (
		<InternalEntryPointContainer
			id={instanceId}
			entryPointReference={panel.entryPointReference}
			fallback={fallback}
			errorFallback={errorFallback}
			// @ts-expect-error - runtimeProps type is inferred from entryPointReference, but we're using dynamic params from panel state
			runtimeProps={params || {}}
		/>
	);

	// If a wrapper is provided, wrap the content
	if (wrapper) {
		return <>{wrapper({ children: entryPointContent })}</>;
	}

	return entryPointContent;
}
